import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel, FieldGroup } from '@/components/ui/field';
import { eventCatalog } from './catalog.generated';
export interface NoteTemplate {
  id: string;
  title: string;
  sections: { heading: string; event_types: string[] }[];
}
export function NoteTemplateEditor({
  value,
  onChange,
}: {
  value: NoteTemplate[];
  onChange: (v: NoteTemplate[]) => void;
}) {
  const patch = (id: string, p: Partial<NoteTemplate>) =>
    onChange(value.map((t) => (t.id === id ? { ...t, ...p } : t)));
  return (
    <details>
      <summary>Clinical note outline templates</summary>
      <div className="flex flex-col gap-4 py-3">
        <p>
          Group confirmed observations under your preferred headings. Other documented observations remain included.
          Templates never infer findings or change measurements.
        </p>
        {value.map((t) => (
          <section key={t.id} className="flex flex-col gap-3 rounded-lg border p-3">
            <Field>
              <FieldLabel htmlFor={`template-${t.id}`}>Template title</FieldLabel>
              <Input
                id={`template-${t.id}`}
                maxLength={120}
                value={t.title}
                onChange={(e) => patch(t.id, { title: e.target.value })}
              />
            </Field>
            {t.sections.map((s, i) => (
              <FieldGroup key={i}>
                <Field>
                  <FieldLabel htmlFor={`heading-${t.id}-${i}`}>Section {i + 1} heading</FieldLabel>
                  <Input
                    id={`heading-${t.id}-${i}`}
                    maxLength={120}
                    value={s.heading}
                    onChange={(e) =>
                      patch(t.id, {
                        sections: t.sections.map((x, j) => (j === i ? { ...x, heading: e.target.value } : x)),
                      })
                    }
                  />
                </Field>
                <details>
                  <summary>Included observations ({s.event_types.length})</summary>
                  <div className="grid gap-2 md:grid-cols-2">
                    {Object.keys(eventCatalog).map((kind) => (
                      <Field orientation="horizontal" key={kind}>
                        <Checkbox
                          id={`${t.id}-${i}-${kind}`}
                          checked={s.event_types.includes(kind)}
                          disabled={t.sections.some((x, j) => j !== i && x.event_types.includes(kind))}
                          onCheckedChange={(v) =>
                            patch(t.id, {
                              sections: t.sections.map((x, j) =>
                                j === i
                                  ? {
                                      ...x,
                                      event_types:
                                        v === true ? [...x.event_types, kind] : x.event_types.filter((k) => k !== kind),
                                    }
                                  : x,
                              ),
                            })
                          }
                        />
                        <FieldLabel htmlFor={`${t.id}-${i}-${kind}`}>
                          {kind.replaceAll('.', ' · ').replaceAll('_', ' ').toLowerCase()}
                        </FieldLabel>
                      </Field>
                    ))}
                  </div>
                </details>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={i === 0}
                    onClick={() => {
                      const sections = [...t.sections];
                      [sections[i - 1], sections[i]] = [sections[i], sections[i - 1]];
                      patch(t.id, { sections });
                    }}>
                    Move section up
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => patch(t.id, { sections: t.sections.filter((_, j) => j !== i) })}>
                    Remove section
                  </Button>
                </div>
              </FieldGroup>
            ))}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={t.sections.length >= 20}
                onClick={() => patch(t.id, { sections: [...t.sections, { heading: '', event_types: [] }] })}>
                Add section
              </Button>
              <Button type="button" variant="outline" onClick={() => onChange(value.filter((x) => x.id !== t.id))}>
                Remove template
              </Button>
            </div>
          </section>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={value.length >= 20}
          onClick={() =>
            onChange([...value, { id: crypto.randomUUID(), title: '', sections: [{ heading: '', event_types: [] }] }])
          }>
          Add note template
        </Button>
      </div>
    </details>
  );
}
